import getConfig from 'next/config';
const { publicRuntimeConfig } = getConfig();
import axios from 'axios';

/**
 * GitHub repository is defined in Next config.
 */
const repository = publicRuntimeConfig.GITHUB_POLICIES.repository;
const branch = publicRuntimeConfig.GITHUB_POLICIES.branch;


/**
 * Get file name based on arguments.
 *
 * @param folder {string}
 * @param type {string}
 * @param clientLang {string}
 * @param clientCountry {string}
 * @returns {string}
 */
function getFileName(folder, type, clientLang, clientCountry) {
    return `${folder}/${type}_${clientCountry}_${clientLang}.md`;
}


/**
 * File paths on GitHub.
 *
 * @param raw {boolean}
 * @param fileName {string}
 * @param repositoryBranch {string|null}
 * @returns {{publicFile: string, rawFile: string}}
 */
function getFileLocationsFromGithub(raw = false, fileName, repositoryBranch = null) {
    const finalBranch = repositoryBranch || branch;
    return {
        rawFile: `https://raw.githubusercontent.com/${repository}/${finalBranch}/${fileName}`,
        publicFile: `https://github.com/${repository}/blob/${finalBranch}/${fileName}`
    }
}


/**
 *
 * We need to get raw markdown file to display it in HTML on our website
 * Todo: There would be nice to do redirect not for public markdown file (because of 404) but on repository home page
 *
 * @param fileLocation {string}
 * @returns {Promise<{redirect: boolean, markdown: null}>}
 */
async function getRawFileFromGithub(fileLocation) {
    let response = {
        markdown: null,
        redirect: true
    }

    try {
        const dataRawFile = await axios.get(fileLocation);
        response.markdown = dataRawFile.data;
        response.redirect = false;
    } catch (err) {
        console.error(`Fetching Github Policies - raw data error:`, err);
        console.log(`File location:`, fileLocation);
    }

    return response;
}

/**
 * We want to get datetime of file last change from git
 * Documentation: https://docs.github.com/en/rest/reference/repos#list-commits
 *
 * @param policyFileName {string}
 * @param repositoryBranch {string}
 * @returns {Promise<{redirectUrl: null, commitDate: null}>}
 */
async function getFileLastChange(policyFileName, repositoryBranch = null) {
    const finalBranch = repositoryBranch || branch;
    const commitUrl = `https://api.github.com/repos/${repository}/commits?sha=${finalBranch}&path=${policyFileName}&page=1&per_page=1`;

    let response = {
        commitDate: null,
        redirectUrl: null
    }

    const githubOpts = {
        headers: {
            authorization: `Bearer ${publicRuntimeConfig.GITHUB_TOKEN}`,
        },
    };

    let commitData;
    try {
        const githubAPI = await axios.get(commitUrl, githubOpts);
        commitData = githubAPI.data[0];
        response.commitDate = await commitData.commit.committer.date;
    } catch (err) {
        response.redirectUrl = true;
        console.error(`Fetching commit date error:`, err?.response?.status);
        console.log(`Commit URL`, commitUrl);
        console.log(`Commit data:`, commitData);
    }

    return response;
}


/**
 * Main function
 * Try to get data from GitHub or redirect visitor to actual markdown file na Github website.
 *
 * @param clientLang {string}
 * @param clientCountry {string}
 * @param folder {string}
 * @param type {string}
 * @param repositoryBranch {string|null}
 * @returns {Promise<{redirectUrl: (string|null), lastChange: null, markdownData: null}>}
 * @constructor
 */
async function FetchPolicy(clientLang, clientCountry, folder, type, repositoryBranch = null) {

    // configuration
    const fileName = getFileName(folder, type, clientLang, clientCountry);
    const fileLocations = getFileLocationsFromGithub(fileName, fileName, repositoryBranch);

    // api calls
    const fileLastChange = await getFileLastChange(fileName, repositoryBranch);
    const fileRawData = await getRawFileFromGithub(fileLocations.rawFile);

    //
    // decision making:
    // we have to redirect user on actual file on Github sites,
    // because some troubles to get file data via API
    //
    let redirectUser = fileRawData.markdown === null || fileLastChange.commitDate === null;

    return {
        markdownData: fileRawData.markdown,
        lastChange: fileLastChange.commitDate,
        redirectUrl: redirectUser ? fileLocations.publicFile : null,
    };
}

export default FetchPolicy;
